'use client';

import { detectImageTypeByBuffer } from '../detect-image-type-by-buffer';

const CONTENT_PRODUCER = '001191110108MACC4D63XF10306';

/**
 * 为抽帧图像添加AIGC元数据
 */
export async function addFrameShotAIGCMetadata(canvas: HTMLCanvasElement, creationId: string): Promise<Blob> {
  const metadata = {
    Label: '1',
    ContentProducer: CONTENT_PRODUCER,
    ContentPropagator: CONTENT_PRODUCER,
    ProduceID: creationId,
    PropagateID: creationId,
    ReservedCode1: creationId,
    ReservedCode2: creationId,
  };
  return addMetadataToPNG(canvas, metadata);
}

/**
 * 在PNG图像中添加元数据到PNG文本块（tEXt chunk）
 * @param canvas HTML画布元素
 * @param metadata 要添加的元数据对象（key-value对）
 * @param keyword 元数据在PNG中的关键字标识符，默认为'AIGC'
 */
export async function addMetadataToPNG(
  canvas: HTMLCanvasElement,
  metadata: Record<string, string>,
  keyword = 'AIGC',
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(async (blob) => {
      if (!blob) {
        reject(new Error('无法生成图像blob'));
        return;
      }

      try {
        const arrayBuffer = await blob.arrayBuffer();
        const uint8Array = new Uint8Array(arrayBuffer);

        // 使用已有的图片类型检测函数验证PNG文件
        const imageType = detectImageTypeByBuffer(arrayBuffer);
        if (imageType !== 'image/png') {
          throw new Error('不是有效的PNG文件');
        }

        // 创建 tEXt chunk 数据
        const textData = createTextChunk(metadata, keyword);

        // 创建 tEXt chunk
        const chunkType = [0x74, 0x45, 0x58, 0x74]; // "tEXt"
        const chunkLength = numberToBigEndian(textData.length, 4);

        // 计算CRC32校验码
        const chunkData = [...chunkType, ...Array.from(textData)];
        let crc = 0xffffffff;
        for (const byte of chunkData) {
          crc = crc ^ byte;
          for (let i = 0; i < 8; i++) {
            crc = crc & 1 ? 0xedb88320 ^ (crc >>> 1) : crc >>> 1;
          }
        }
        crc = crc ^ 0xffffffff;
        const crcBytes = numberToBigEndian(crc >>> 0, 4);

        // 构建完整的 tEXt chunk
        const textChunk = [...chunkLength, ...chunkType, ...Array.from(textData), ...crcBytes];

        // 找到IHDR chunk后的位置插入 tEXt chunk
        let insertPosition = 8; // PNG签名后

        // 跳过IHDR chunk (8字节签名 + 4字节长度 + 4字节类型 + 13字节数据 + 4字节CRC = 33字节)
        if (uint8Array.length >= 33) {
          insertPosition = 33;
        }

        // 创建新的PNG数据
        const newPngData = new Uint8Array(uint8Array.length + textChunk.length);
        newPngData.set(uint8Array.slice(0, insertPosition), 0);
        newPngData.set(textChunk, insertPosition);
        newPngData.set(uint8Array.slice(insertPosition), insertPosition + textChunk.length);

        // 创建新的blob
        const newBlob = new Blob([newPngData], { type: 'image/png' });
        resolve(newBlob);
      } catch (error) {
        reject(error);
      }
    }, 'image/png');
  });
}

/**
 * 创建PNG tEXt chunk数据
 * @param metadata 要添加的元数据对象
 * @param keyword 元数据关键字标识符
 */
export function createTextChunk(metadata: Record<string, string>, keyword: string): Uint8Array {
  // 将元数据序列化为JSON字符串
  const metadataValue = JSON.stringify(metadata);

  // tEXt chunk 格式：keyword + null separator + text
  const keywordBytes = stringToUTF8Bytes(keyword);
  const nullSeparator = [0x00];
  const valueBytes = stringToUTF8Bytes(metadataValue);

  // 组合完整的 tEXt chunk 数据
  const textData = [...keywordBytes, ...nullSeparator, ...valueBytes];

  return new Uint8Array(textData);
}

/**
 * 将数值转换为大端字节序的字节数组
 */
function numberToBigEndian(num: number, bytes: number): number[] {
  const result: number[] = [];
  for (let i = bytes - 1; i >= 0; i--) {
    result.push((num >> (i * 8)) & 0xff);
  }
  return result;
}

/**
 * 将字符串转换为UTF-8字节数组
 */
function stringToUTF8Bytes(str: string): number[] {
  const encoder = new TextEncoder();
  return Array.from(encoder.encode(str));
}
